import { Precision } from "@framework/provider-asu";

import { SELECTED_ITEM_TYPE } from "../../../../utils";
import { IResponsePoint, IResponseTrain, JournalRecorderVisualItem } from "./interfaces";

export interface IJournalBaseRequestData {
  idTopLevel: number;
  startTimestamp: string;
  endTimestamp: string;
  precision: Precision;
}

export interface IJournalPageRequestData extends IJournalBaseRequestData {
  skip: number;
  take: number;
}

interface IJournalRequestApi {
  fetchJson: (args: {
    url: string;
    method: "GET";
    data: IJournalBaseRequestData | IJournalPageRequestData;
    signal?: AbortSignal;
  }) => Promise<unknown>;
}

interface IJournalVisualRequest {
  selectedItemType: SELECTED_ITEM_TYPE;
  data: IJournalBaseRequestData;
  signal?: AbortSignal;
}

interface IJournalSnapshotRequest extends IJournalVisualRequest {
  pageData: IJournalPageRequestData;
  journalSignal?: AbortSignal;
  includeVisual: boolean;
}

type TJournalResponse = IResponseTrain[] | IResponsePoint[];
export type TJournalVisualResponse = JournalRecorderVisualItem[];
export type TJournalVisualTransition = "show" | "hide" | null;
export type TJournalSnapshotTrigger = "context-change" | "reload" | null;

interface IJournalSnapshotTriggerOptions {
  requestedContextKey: string;
  currentContextKey: string;
  forceReload: boolean;
  isRecorderMode: boolean;
}

export const shouldFetchJournalVisual = (hasAdvancedFeaturesAccess: boolean, isVisualOpen: boolean): boolean =>
  hasAdvancedFeaturesAccess && isVisualOpen;

export const getJournalVisualTransition = (wasVisualOpen: boolean, isVisualOpen: boolean): TJournalVisualTransition =>
  wasVisualOpen === isVisualOpen ? null : isVisualOpen ? "show" : "hide";

export const getJournalSnapshotTrigger = ({
  requestedContextKey,
  currentContextKey,
  forceReload,
  isRecorderMode,
}: IJournalSnapshotTriggerOptions): TJournalSnapshotTrigger => {
  if (isRecorderMode || !currentContextKey) {
    return null;
  }

  if (requestedContextKey !== currentContextKey) {
    return "context-change";
  }

  return forceReload ? "reload" : null;
};

export const fetchJournalVisual = async (
  api: IJournalRequestApi,
  request: IJournalVisualRequest,
): Promise<TJournalVisualResponse> => {
  const response = await api.fetchJson({
    url: `/visual/${request.selectedItemType}`,
    method: "GET",
    data: request.data,
    signal: request.signal,
  });

  return (response || []) as TJournalVisualResponse;
};

export const fetchJournalSnapshot = async (
  api: IJournalRequestApi,
  request: IJournalSnapshotRequest,
): Promise<{ journalJson: TJournalResponse; visualJson: TJournalVisualResponse }> => {
  const journalRequest = api.fetchJson({
    url: `/journal/${request.selectedItemType}`,
    method: "GET",
    data: request.pageData,
    signal: request.journalSignal,
  });
  const visualRequest = request.includeVisual ? fetchJournalVisual(api, request) : Promise.resolve([]);

  const [journalResponse, visualJson] = await Promise.all([journalRequest, visualRequest]);

  return {
    journalJson: (journalResponse || []) as TJournalResponse,
    visualJson,
  };
};
